import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Divider, Paper, Switch, Typography } from '@mui/material';
import { DataProviderInstance } from '../../../api/DataProvider';

const errorMessageOf = (response, fallback) => (response && (response.error || response.message)) || fallback;

// Cada assunto que a pessoa pode desligar (colunas de user_communication_preferences)
const TOPICS = [
  {
    key: 'product_updates_email',
    label: 'Novidades da Gabriela por e-mail',
    on: 'Você vai receber as novidades por e-mail.',
    off: 'Você não vai mais receber as novidades por e-mail.',
  },
  {
    key: 'onboarding_email',
    label: 'Dicas de uso por e-mail',
    on: 'Você vai receber as dicas de uso por e-mail.',
    off: 'Você não vai mais receber as dicas de uso por e-mail.',
  },
];

const DEFAULT_PREFERENCES = { product_updates_email: true, onboarding_email: true };

const readPreferences = (preferences) => TOPICS.reduce((acc, topic) => ({
  ...acc,
  [topic.key]: preferences ? preferences[topic.key] !== false : DEFAULT_PREFERENCES[topic.key],
}), {});

/**
 * Comunicações do usuário logado: liga ou desliga as Novidades e as dicas de uso por e-mail
 * (user/profile/*_communication_preferences.php). Avisos operacionais nunca são cortados.
 *
 * Props:
 *   onFeedback(message, severity) - mostra o aviso no snackbar da página
 */
const CommunicationPreferences = ({ onFeedback }) => {
  const { GetCommunicationPreferences, UpdateCommunicationPreferences } = DataProviderInstance();
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState('');

  useEffect(() => {
    GetCommunicationPreferences((response) => {
      if (response && response.success && response.preferences) {
        setPreferences(readPreferences(response.preferences));
        setLoadError('');
      } else {
        setLoadError(errorMessageOf(response, 'Não foi possível carregar suas preferências.'));
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = (topic) => (event) => {
    const next = event.target.checked;
    setPreferences((prev) => ({ ...prev, [topic.key]: next }));
    setSaving(topic.key);
    UpdateCommunicationPreferences({ [topic.key]: next }, (response) => {
      setSaving('');
      if (response && response.success) {
        const saved = response.preferences ? response.preferences[topic.key] !== false : next;
        setPreferences((prev) => ({ ...prev, [topic.key]: saved }));
        onFeedback && onFeedback(saved ? topic.on : topic.off, 'success');
      } else {
        setPreferences((prev) => ({ ...prev, [topic.key]: !next }));
        onFeedback && onFeedback(errorMessageOf(response, 'Não foi possível salvar sua preferência.'), 'error');
      }
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.03)',
        border: '1px solid #E2E8F0'
      }}
    >
      <Typography variant="h6" fontWeight={600}>
        Comunicações
      </Typography>
      <Divider sx={{ margin: '16px 0' }} />

      <Box sx={{ display: 'grid', gap: 2 }}>
        {TOPICS.map((topic) => (
          <Box key={topic.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Typography component="label" htmlFor={`communication-${topic.key}`} sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
              {topic.label}
            </Typography>
            {loading ? (
              <CircularProgress size={22} />
            ) : (
              <Switch
                id={`communication-${topic.key}`}
                checked={preferences[topic.key]}
                onChange={handleToggle(topic)}
                disabled={Boolean(saving) || Boolean(loadError)}
              />
            )}
          </Box>
        ))}
        <Typography sx={{ fontSize: '0.8rem', color: 'var(--color-text-light)' }}>
          {loadError || 'Avisos da sua conta, cobrança e segurança continuam chegando.'}
        </Typography>
      </Box>
    </Paper>
  );
};

export default CommunicationPreferences;
